import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import AddNewAgeGroup from "./AddNewAgeGroup";
import EditAgeGroup from "./EditAgeGroup";
import API_PATHS from "src/api/apiPaths";
import API_HEADERS from "src/api/apiConfig";
import ListResponse from "src/helpers/ListResponse";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import Loading from "src/helpers/Loading";
import SearchFilter from "src/helpers/SearchFilter";
import CustomPagination from "src/helpers/CustomPaginate";
import * as ConfirmPopup from "../../../../helpers/ConfirmPopup";
import { PermissionCheck } from "src/helpers/PermissionCheck";

const AgeGroup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // List
  const [ageGroups, setAgeGroups] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);

  // Modals
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [modalId, setModalId] = useState<number | null>(null);

  const getAgeGroups = async (): Promise<void> => {
    let request: any = API_PATHS.foodAgeGroupsList;

    let searchParams: any = {};

    if (activePage > 1) {
      searchParams["page"] = activePage;
    }

    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setAgeGroups(response.foodAgeGroup);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      navigate(-1);
    }
  };

  useEffect(() => {
    getAgeGroups();
  }, [activePage]);

  const deleteAgeGroup = (ageGroupId: number, ageGroupTitle: string) => {
    ConfirmPopup.deletePopup(
      t("meal_age_groups_delete_title"),
      t("meal_age_groups_delete_type"),
      ageGroupTitle
    ).then(async (response) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.foodAgeGroupsDelete(ageGroupId),
            "DELETE",
            API_HEADERS.authenticated
          );
          getAgeGroups();
          FLASH_MESSAGES.successMsg(response.success);
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  return (
    <>
      {PermissionCheck("food-age-group-store") ? (
        <div className="w-100 mb-3 d-flex justify-content-end">
          <button
            className="button-blue has-icon"
            onClick={() => setShowAddModal(true)}
          >
            <i className="icon add-icon" />
            {t("meal_add_new_age_group")}
          </button>
          {showAddModal && (
            <AddNewAgeGroup
              onShowModal={showAddModal}
              setShowModal={(e) => setShowAddModal(e)}
              setModified={() => getAgeGroups()}
            />
          )}
        </div>
      ) : (
        ""
      )}
      <div className="table-list mb-5">
        <table role="table" className="w-100">
          <thead>
            <tr role="row">
              <th
                style={{ width: "40%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon category-icon" />
                {t("meal_age_groups_table_name")}
              </th>
              <th
                style={{ width: "25%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon age-group-icon" />
                {t("meal_age_groups_table_age_group")}
              </th>
              <th
                style={{ width: "20%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon season-icon" />
                {t("meal_age_groups_table_season")}
              </th>
              <th
                style={{ width: "15%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon actions-icon" />
                {t("meal_age_groups_table_action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan={4}>
                  <Loading />
                </td>
              </tr>
            ) : (
              ageGroups?.data?.map((ageGroup: any) => (
                <tr key={ageGroup.id}>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_age_groups_table_name")}
                  >
                    {ageGroup.name}
                  </td>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_age_groups_table_age_group")}
                  >
                    {ageGroup.age}
                  </td>
                  <td
                    role="cell"
                    className="text-left bold-text"
                    data-label={t("meal_age_groups_table_season")}
                  >
                    {ageGroup.season}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("meal_age_groups_table_action")}
                  >
                    <div className="actions">
                      {PermissionCheck("food-age-group-update") &&
                      PermissionCheck("food-age-group-edit") ? (
                        <button
                          className="button-unstyled-icon"
                          onClick={() => {
                            setShowEditModal(true);
                            setModalId(ageGroup.id);
                          }}
                        >
                          <i className="icon edit-icon" />
                        </button>
                      ) : (
                        "-"
                      )}
                      {showEditModal && modalId === ageGroup.id && (
                        <EditAgeGroup
                          onShowModal={showEditModal}
                          setShowModal={(e) => {
                            setShowEditModal(e);
                            setModalId(null);
                          }}
                          ageGroupId={ageGroup.id}
                          setModified={() => getAgeGroups()}
                        />
                      )}
                      {PermissionCheck("food-age-group-delete") ? (
                        <button
                          className="button-unstyled-icon"
                          onClick={() =>
                            deleteAgeGroup(ageGroup.id, ageGroup.name)
                          }
                        >
                          <i className="icon delete-icon" />
                        </button>
                      ) : (
                        "-"
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <ListResponse totalItems={ageGroups?.data?.length} loading={loading} />
      </div>
      <CustomPagination
        data={ageGroups}
        setActivePage={(e) => setActivePage(e)}
      />
    </>
  );
};

export default AgeGroup;
